import React, { useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Typography, Alert, Stack, Divider
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { travelerScreeningAPI } from '../../../services/api';

interface Props {
  onExtracted: (data: Record<string, unknown>) => void;
}

const PassportUploadStep: React.FC<Props> = ({ onExtracted }) => {
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [preview, setPreview]   = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setPreview(URL.createObjectURL(file));
    setLoading(true);
    try {
      const data = await travelerScreeningAPI.uploadPassport(file, 'passport');
      onExtracted(data);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || 'OCR failed. Please try a clearer image.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h6" gutterBottom>Upload Passport</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The photo page will be scanned. The face will be automatically masked before storage.
      </Typography>

      {preview && (
        <Box sx={{ mb: 2 }}>
          <img src={preview} alt="preview" style={{ maxHeight: 200, borderRadius: 8, border: '1px solid #e0e0e0' }} />
        </Box>
      )}

      {loading ? (
        <Box sx={{ my: 3 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>Extracting fields…</Typography>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<CameraAltIcon />}
            size="large"
            onClick={() => cameraInputRef.current?.click()}
            sx={{ minWidth: 180 }}
          >
            Take Photo
          </Button>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            size="large"
            onClick={() => fileInputRef.current?.click()}
            sx={{ minWidth: 180 }}
          >
            Upload from Device
          </Button>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {/* Camera input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
      />
      {/* File input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
      />
    </Box>
  );
};

export default PassportUploadStep;
